// src/modules/quotes/QuoteManagement.jsx
import { useState, useEffect, useMemo } from "react";
import { C } from "../../constants";
import { apiCall } from "../../api";
import QuoteDetail from "./QuoteDetail";
import CreateQuoteForm from "./CreateQuoteForm";

// ─── Date helper: any string → DD/MM/YYYY ───
function fmtDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return val;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ─── Expiry pill — shows DD/MM/YYYY + Valid/Expired ───
function ExpiryPill({ expiryDate }) {
  if (!expiryDate) return <span style={{ color: C.t3, fontSize: 13 }}>—</span>;
  const expired = new Date() > new Date(expiryDate);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
        padding: "2px 8px", borderRadius: 999, display: "inline-block",
        background: expired ? "#FEE2E2" : "#D1FAE5",
        color: expired ? "#991B1B" : "#065F46",
      }}>
        {expired ? "Expired" : "Valid"}
      </span>
      <span style={{ fontSize: 11, color: C.t3 }}>{fmtDate(expiryDate)}</span>
    </div>
  );
}

// ─── Status pill with toggle ───
const STATUS_OPTIONS = ["Open", "Closed - Lost", "Ordered"];
const STATUS_COLORS = {
  "Open":          { bg: "#DBEAFE", c: "#1E40AF" },
  "Closed - Lost": { bg: "#FEE2E2", c: "#991B1B" },
  "Ordered":       { bg: "#D1FAE5", c: "#065F46" },
};

function StatusPill({ status, quoteId, expiryDate, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const s = STATUS_COLORS[status] || STATUS_COLORS["Open"];
  const isExpired = expiryDate && new Date() > new Date(expiryDate);

  const handleSelect = (newStatus) => {
    setOpen(false);
    if (newStatus === status) return;
    if (newStatus === "Ordered" && isExpired) {
      alert("This quote has expired. Please submit a Quote Update Request to verify pricing and accuracy before placing an order.");
      return;
    }
    onStatusChange(quoteId, newStatus);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
          letterSpacing: "0.04em", textTransform: "uppercase", cursor: "pointer",
          border: "none", background: s.bg, color: s.c, fontFamily: "inherit",
          display: "flex", alignItems: "center", gap: 4,
        }}>
        {status} <span style={{ fontSize: 8 }}>▾</span>
      </button>
      {open && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setOpen(false)} />
          <div style={{ position: "absolute", top: "110%", left: 0, zIndex: 100,
            background: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: 150, overflow: "hidden",
          }}>
            {STATUS_OPTIONS.map(opt => {
              const sc = STATUS_COLORS[opt];
              return (
                <button key={opt} onClick={() => handleSelect(opt)}
                  style={{ display: "block", width: "100%", padding: "9px 14px", textAlign: "left",
                    border: "none", background: opt === status ? C.grayBg : C.white,
                    cursor: "pointer", fontSize: 13, color: C.t1, fontFamily: "inherit",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
                  onMouseLeave={e => e.currentTarget.style.background = opt === status ? C.grayBg : C.white}>
                  <span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.c, marginRight: 8 }}>
                    {opt}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Component ───
export default function QuoteManagement({ user }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filterBy, setFilterBy] = useState("");
  const [filterField, setFilterField] = useState("all");
  const [filterExpiry, setFilterExpiry] = useState("all"); // all | valid | expired
  const [filterStatus, setFilterStatus] = useState("all"); // all | Open | Closed - Lost | Ordered

  const isAdmin = (user?.role || "").toUpperCase() === "ADMIN";

  // FIX 6: collect all requestIds already linked to a quote
  const usedRequestIds = useMemo(() => new Set(quotes.map(q => q.requestId).filter(Boolean)), [quotes]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadQuotes(); }, []);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const r = await apiCall("getQuotes", { email: user.email, company: user.company, role: user.role || "CLIENT" });
      if (r.success) setQuotes(r.quotes || []);
    } catch (err) { console.error("Failed to load quotes:", err); }
    setLoading(false);
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await apiCall("updateQuoteStatus", { quoteId, status: newStatus });
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
      if (selectedQuote?.id === quoteId) setSelectedQuote(prev => ({ ...prev, status: newStatus }));
    } catch (err) { console.error("Failed to update status:", err); }
  };

  const filtered = useMemo(() => {
    return quotes.filter(q => {
      // Text filter
      if (filterBy.trim()) {
        const term = filterBy.toLowerCase();
        const textMatch = filterField === "quoteNumber" ? (q.quoteNumber || "").toLowerCase().includes(term)
          : filterField === "company" ? (q.company || "").toLowerCase().includes(term)
          : filterField === "requestedBy" ? (q.requestedBy || "").toLowerCase().includes(term)
          : filterField === "manufacturer" ? (q.manufacturer || "").toLowerCase().includes(term)
          : (q.quoteNumber || "").toLowerCase().includes(term)
            || (q.company || "").toLowerCase().includes(term)
            || (q.requestedBy || "").toLowerCase().includes(term)
            || (q.manufacturer || "").toLowerCase().includes(term);
        if (!textMatch) return false;
      }
      // Expiry filter
      if (filterExpiry !== "all") {
        const expired = q.expiryDate && new Date() > new Date(q.expiryDate);
        if (filterExpiry === "valid" && expired) return false;
        if (filterExpiry === "expired" && !expired) return false;
      }
      // Status filter
      if (filterStatus !== "all" && (q.status || "Open") !== filterStatus) return false;
      return true;
    });
  }, [quotes, filterBy, filterField, filterExpiry, filterStatus]);

  const fmt = (val) => {
    if (!val && val !== 0) return "—";
    return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={{ width: "100%" }}>

      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 4px" }}>Quote Management</h1>
          <p style={{ fontSize: 14, color: C.t2, margin: 0 }}>Track and manage all quotes in one place.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <select value={filterField} onChange={e => setFilterField(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Fields</option>
            <option value="quoteNumber">Quote #</option>
            <option value="requestedBy">Requested By</option>
            <option value="company">Company</option>
            <option value="manufacturer">Manufacturer</option>
          </select>
          <select value={filterExpiry} onChange={e => setFilterExpiry(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Expiry</option>
            <option value="valid">Valid</option>
            <option value="expired">Expired</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Status</option>
            <option value="Open">Open</option>
            <option value="Closed - Lost">Closed - Lost</option>
            <option value="Ordered">Ordered</option>
          </select>
          <input type="text" placeholder="Filter..." value={filterBy} onChange={e => setFilterBy(e.target.value)}
            style={{ padding: "7px 12px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, outline: "none", background: C.grayBg, fontFamily: "inherit", width: 160 }} />
          {filterBy && <button onClick={() => setFilterBy("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 18, lineHeight: 1 }}>×</button>}
          {isAdmin && (
            <button onClick={() => setShowCreate(true)}
              style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
              + Create Quote
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", width: "100%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "130px 110px 1fr 1fr 1fr 110px 120px 130px", gap: 12, padding: "10px 20px", background: C.grayBg, borderBottom: `1px solid ${C.border}` }}>
          {["Quote #", "Quoted Date", "Requested By", "Company", "Manufacturer", "Amount", "Expiry", "Status"].map(col => (
            <span key={col} style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>{col}</span>
          ))}
        </div>

        <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 280px)" }}>
          {loading ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: C.t3, fontSize: 14 }}>Loading quotes...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: C.t3, fontSize: 14 }}>
              {quotes.length === 0 ? "No quotes yet." : "No quotes match your filter."}
            </div>
          ) : (
            filtered.map((q, i) => (
              <div key={q.id}
                onClick={() => setSelectedQuote(q)}
                style={{ display: "grid", gridTemplateColumns: "130px 110px 1fr 1fr 1fr 110px 120px 130px", gap: 12, padding: "13px 20px", borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer", transition: "background 0.15s", alignItems: "center" }}
                onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.info }}>{q.quoteNumber}</span>
                {/* FIX 1: DD/MM/YYYY for quoted date, sort uses full timestamp */}
                <span style={{ fontSize: 12, color: C.t3 }}>{fmtDate(q.quotedDate)}</span>
                <span style={{ fontSize: 13, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.requestedBy || "—"}</span>
                <span style={{ fontSize: 13, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.company || "—"}</span>
                <span style={{ fontSize: 13, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.manufacturer || "—"}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{fmt(q.amount)}</span>
                <div onClick={e => e.stopPropagation()}>
                  <ExpiryPill expiryDate={q.expiryDate} />
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <StatusPill status={q.status || "Open"} quoteId={q.id} expiryDate={q.expiryDate} onStatusChange={handleStatusChange} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {selectedQuote && (
        <QuoteDetail
          quote={selectedQuote}
          isAdmin={isAdmin}
          user={user}
          onClose={() => setSelectedQuote(null)}
          onStatusChange={handleStatusChange}
          onUpdate={(updated) => {
            setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
            setSelectedQuote(updated);
          }}
        />
      )}

      {showCreate && (
        <CreateQuoteForm
          user={user}
          usedRequestIds={usedRequestIds}
          onClose={() => setShowCreate(false)}
          onCreate={(newQuote) => {
            setQuotes(prev => [newQuote, ...prev]);
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}
